/**
 * Browser speech-to-text (CCA-9.3) — dictates an editable message into the
 * composer using the Web Speech API. Technology-agnostic: if the API is
 * unavailable or permission is denied, callers fall back to typing.
 *
 * V1 is dictation only — the transcript lands in the composer for the user to
 * review and send; it never auto-sends, preserving the confirmation guarantee.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// The Web Speech API isn't in the standard DOM lib types — keep it loose.
/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyRecognition = any;

const getRecognitionCtor = (): AnyRecognition | null => {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
};

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': "Microphone access was blocked. You can type your request instead.",
  'service-not-allowed': "Microphone access was blocked. You can type your request instead.",
  'no-speech': "I didn't catch anything — tap the mic to try again, or type instead.",
  'audio-capture': "No microphone was found. You can type your request instead.",
  'network': "Voice input needs a connection right now — please type instead.",
  'language-not-supported': "I couldn't transcribe that language — please type instead.",
};

export interface SpeechToText {
  supported: boolean;
  listening: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
  clearError: () => void;
}

export function useSpeechToText(onTranscript: (text: string) => void): SpeechToText {
  const Ctor = getRecognitionCtor();
  const supported = !!Ctor;
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<AnyRecognition | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!Ctor) { setError('Voice input isn’t supported in this browser — please type instead.'); return; }
    if (recRef.current) { stop(); return; }
    setError(null);
    const rec: AnyRecognition = new Ctor();
    rec.lang = 'en-US';
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    rec.onresult = (e: any) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      onTranscriptRef.current(transcript.trim());
    };
    rec.onerror = (e: any) => {
      setError(ERROR_MESSAGES[e?.error] ?? "I couldn't transcribe that — please type instead.");
    };
    rec.onend = () => { recRef.current = null; setListening(false); };

    try {
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch {
      recRef.current = null;
      setListening(false);
      setError("Couldn't start voice input — please type instead.");
    }
  }, [Ctor, stop]);

  const clearError = useCallback(() => setError(null), []);

  // Clean up any active recognition on unmount.
  useEffect(() => () => { try { recRef.current?.abort?.(); } catch { /* noop */ } }, []);

  return { supported, listening, error, start, stop, clearError };
}
